import React from 'react';
import {
  Modal,
  Stack,
  Text,
  TextInput,
  Button,
  Group,
  Table,
  ScrollArea,
  Loader,
  Flex,
  Pagination,
  Checkbox,
  Badge
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { assessmentScheduleService } from 'services/lia/assessmentScheduleService';

interface CopyAssessmentScheduleModalProps {
  opened: boolean;
  onClose: () => void;
  onScheduleSelected: (_scheduleId: string) => void;
}

type ScheduleOption = {
  msAssessmentScheduleID: string;
  name: string;
  type: string;
  programName?: string;
  startTime: string;
  endTime: string;
};

const CopyAssessmentScheduleModal: React.FC<CopyAssessmentScheduleModalProps> = ({
  opened,
  onClose,
  onScheduleSelected
}) => {
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selectedScheduleId, setSelectedScheduleId] = React.useState<string | null>(null);

  // Reset when modal opens/closes
  React.useEffect(() => {
    if (!opened) {
      setSearchKeyword('');
      setPage(1);
      setSelectedScheduleId(null);
    }
  }, [opened]);

  const { data, isFetching } = useQuery({
    queryKey: ['copy-assessment-schedule-modal', searchKeyword, page],
    queryFn: () =>
      assessmentScheduleService.getList({
        search: searchKeyword,
        page: page,
        limit: 10
      }),
    placeholderData: (previousData) => previousData,
    enabled: opened
  });

  const schedules: ScheduleOption[] = (data?.data || []).map((item: any) => ({
    msAssessmentScheduleID: item.msAssessmentScheduleID,
    name: item.name || '-',
    type: item.type || '-',
    programName: item.programName,
    startTime: item.startTime,
    endTime: item.endTime
  }));

  const totalPages = data?.totalPages || 1;

  const handleConfirm = () => {
    if (selectedScheduleId) {
      onScheduleSelected(selectedScheduleId);
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size='lg'>
          Copy From Other Assessment Schedule
        </Text>
      }
      size='xl'
      centered
    >
      <Stack gap='lg'>
        <Text size='sm' c='dimmed'>
          Select an assessment schedule to copy its details to the current form. This will populate
          all fields with the selected schedule's information.
        </Text>

        <TextInput
          placeholder='Search schedules by name, type, or program'
          value={searchKeyword}
          onChange={(event) => {
            setSearchKeyword(event.currentTarget.value);
            setPage(1);
          }}
          leftSection={<IconSearch size={16} stroke={1.6} />}
          radius='md'
        />

        {isFetching ? (
          <Flex justify='center' align='center' h={200}>
            <Loader size='md' />
          </Flex>
        ) : schedules.length === 0 ? (
          <Stack align='center' gap='md' py='xl'>
            <Text size='sm' c='dimmed' ta='center'>
              No assessment schedules found.
            </Text>
          </Stack>
        ) : (
          <ScrollArea h={400} offsetScrollbars>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 70 }}>Select</Table.Th>
                  <Table.Th>Schedule Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Program</Table.Th>
                  <Table.Th>Period</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {schedules.map((schedule) => (
                  <Table.Tr key={schedule.msAssessmentScheduleID}>
                    <Table.Td>
                      <Checkbox
                        checked={selectedScheduleId === schedule.msAssessmentScheduleID}
                        onChange={() => {
                          setSelectedScheduleId(
                            selectedScheduleId === schedule.msAssessmentScheduleID
                              ? null
                              : schedule.msAssessmentScheduleID
                          );
                        }}
                        aria-label={`Select ${schedule.name}`}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text fw={600} size='sm'>
                          {schedule.name}
                        </Text>
                        <Text size='xs' c='dimmed'>
                          {schedule.msAssessmentScheduleID}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant='light' size='sm'>
                        {schedule.type}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{schedule.programName || '-'}</Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size='xs'>{formatDate(schedule.startTime)}</Text>
                        <Text size='xs' c='dimmed'>
                          to {formatDate(schedule.endTime)}
                        </Text>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        <Group justify='center'>
          <Pagination total={Math.max(totalPages, 1)} value={page} onChange={setPage} />
        </Group>

        <Group justify='flex-end'>
          <Button variant='default' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedScheduleId}>
            Copy Schedule Data
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CopyAssessmentScheduleModal;
